<template>
  <div class="pa-2 bg-grey-lighten-4 rounded-lg">
    <v-container class="pa-0 pb-2">
      <v-row>
        <v-col class="text-primary"> Assigned To </v-col>
      </v-row>
      <v-row v-if="!isValid">
        <v-col>
          <v-input :error-messages="errorMessages" :error="!isValid" :model-value="props.modelValue"></v-input>
        </v-col>
      </v-row>
    </v-container>
    <v-container class="pa-0">
      <v-row>
        <v-col cols="12">
          <component
            :is="actualComponent"
            ref="autocompleteRef"
            v-model="selectedUsers"
            v-model:search="searchTerm"
            :items="autocompleteItems"
            :loading="loading"
            :label="props.label"
            :disabled="props.disabled"
            multiple
            chips
            closable-chips
            :menu-props="{ maxWidth: '100%' }"
            placeholder="Search for a user..."
            @update:model-value="onSelectionChange"
            @update:search="onSearchUpdate">
            <template #chip="{ props: chipProps, item }">
              <v-chip v-bind="chipProps" :closable="!props.disabled" @click:close="removeUser(item.value)">
                {{ typeof item.value === "object" && item.value?.fullName != null ? item.value.fullName : item.value }}
              </v-chip>
            </template>
            <template #item="{ props: itemProps, item }">
              <v-list-item v-bind="itemProps" :title="item.title" />
            </template>
          </component>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script setup>
import { watch, ref, computed, nextTick } from "vue";
import { VAutocomplete } from "vuetify/components";
import debounce from "lodash.debounce";

const props = defineProps({
  remoteSearch: {
    type: Function,
    default: () => () => Promise.resolve([]),
  },
  label: {
    type: String,
    required: true,
  },
  items: {
    type: Array,
    default: () => [],
  },
  component: {
    type: [String, Object],
    default: null,
  },
  modelValue: {
    type: Array,
    required: true,
  },
  rules: {
    type: Array,
    default: () => [],
  },
  // childRules not used - selections are from dropdown only, so always valid
  // Declared as no-op prop to suppress Vue warnings when passed via vBind
  childRules: {
    type: Array,
    default: () => [],
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});

// Use the provided component or default to v-autocomplete
const actualComponent = computed(() => props.component || VAutocomplete);

const emit = defineEmits(["update:modelValue"]);

const autocompleteRef = ref(null);
const searchTerm = ref("");
const loading = ref(false);
const searchResults = ref([]);
const isValid = ref(true);
const errorMessages = ref([]);

// Normalize a single entry to a string. Handles legacy object values
// (e.g. {id, fullName}) that may have been persisted by earlier code.
const normalizeEntry = (entry) => {
  if (typeof entry === "string") return entry;
  if (typeof entry === "object" && entry !== null) {
    return entry.fullName ?? entry.full_name ?? (entry.id != null ? String(entry.id) : "");
  }
  return "";
};

// Computed selected users that syncs with modelValue
// Defensive: ensure we always return an array and normalize any legacy object entries to strings
const selectedUsers = computed({
  get: () => {
    const raw = Array.isArray(props.modelValue) ? props.modelValue : [];
    return raw.map(normalizeEntry);
  },
  set: (value) => emit("update:modelValue", Array.isArray(value) ? value : []),
});

// Helper: are two values the same (by id for objects, by reference for primitives)?
const valueEquals = (a, b) => {
  if (a === b) return true;
  if (a?.id != null && b?.id != null) return a.id === b.id;
  return false;
};

// Combine search results with static items, excluding already selected users
const autocompleteItems = computed(() => {
  const staticItems = props.items.map((item) => {
    if (typeof item === "string") {
      return { title: item, value: item };
    }
    return item;
  });

  // Include loaded modelValue entries that are objects (e.g. ClientCompanyUser) so they display before search
  const currentValue = Array.isArray(props.modelValue) ? props.modelValue : [];
  const fromModel =
    currentValue
      .filter((user) => typeof user === "object" && user !== null && (user.fullName != null || user.id != null))
      .map((user) => ({
        title: user.fullName ?? String(user.id),
        value: { ...user },
      })) ?? [];
  const allItems = [...staticItems];
  for (const item of fromModel) {
    if (!allItems.some((existing) => valueEquals(existing.value, item.value))) {
      allItems.push(item);
    }
  }

  // Merge search results, avoiding duplicates (by id when value is object)
  for (const result of searchResults.value) {
    const exists = allItems.some((item) => valueEquals(item.value, result.value));
    if (!exists) {
      allItems.push(result);
    }
  }

  // Filter out already selected users from dropdown
  return allItems.filter((item) => !currentValue.some((sel) => valueEquals(sel, item.value)));
});

// Debounced search function
const debouncedSearch = debounce(async (value) => {
  if (!value || !props.remoteSearch) {
    loading.value = false;
    searchResults.value = [];
    return;
  }

  try {
    const results = await props.remoteSearch(value);
    searchResults.value = results || [];
  } catch {
    searchResults.value = [];
  } finally {
    loading.value = false;
  }
}, 300);

const onSearchUpdate = (value) => {
  loading.value = true;
  debouncedSearch(value);
};

const onSelectionChange = (newSelection) => {
  emit("update:modelValue", newSelection);
  // Clear search after selection to allow searching for another user
  nextTick(() => {
    searchTerm.value = "";
    searchResults.value = [];
  });
};

const removeUser = (userValue) => {
  const currentValue = Array.isArray(props.modelValue) ? props.modelValue : [];
  const updatedUsers = currentValue.filter((user) => !valueEquals(user, userValue));
  emit("update:modelValue", updatedUsers);
};

const validate = () => {
  errorMessages.value = [];
  // Normalize before validating so legacy object entries don't crash string-based rules
  const currentValue = (Array.isArray(props.modelValue) ? props.modelValue : []).map(normalizeEntry);
  for (const rule of props.rules) {
    const result = rule(currentValue);
    if (result !== true) {
      errorMessages.value.push(result);
      isValid.value = false;
      return false;
    }
  }
  isValid.value = true;
  return true;
};

watch(() => props.modelValue, validate, { deep: true });
watch(() => props.rules, validate, { immediate: true });

defineExpose({
  validate,
  isValid,
  errorMessages,
});
</script>
